import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import AddIcons from '@/icons/AddIcons'
import BlockIcons from '@/icons/BlockIcons'
import CheckIcons from '@/icons/CheckIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import MailIcons from '@/icons/MailIcons'
import NonInvoicableIcons from '@/icons/NonInvoicableIcons'
import PaidIcons from '@/icons/PaidIcons'
import SendIcons from '@/icons/SendIcons'
import UndoIcons from '@/icons/UndoIcons'
import { AddInvoiceUpdateProps } from '@/modals/InvoiceModals/AddInvoiceUpdates'
import { CancelOrDeleteProps } from '@/modals/InvoiceModals/CancelOrDelete'
import { SignedDocumentModalProps } from '@/modals/InvoiceModals/SignedDocument'
import { PreviewInvoiceModalProps } from '@/modals/InvoiceModals/PreviewInvoice'
import { RevenueAllocationPreviewProps } from '@/modals/InvoiceModals/RevenueAllocationPreview'
import { RollbackInvoiceProps } from '@/modals/InvoiceModals/RollbackInvoice'
import { SendMailModalProps } from '@/modals/InvoiceModals/SendMail'
import type { Action } from '@/components/ButtonGroupActions'
import { paths } from '@/utils/path'
import { BarChart, FileSignature } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { INVOICE_STATUS_OPTIONS, isInvoiceConfirmable } from '../constants'
import {
    SIGNED_DOCUMENT_TYPES,
    SIGNED_DOCUMENTS,
} from '../constants/signed-documents'
import { approvedInvoice, cancalledInvoice, deleteInvoice } from '../services'
import { InvoiceDetail } from '../types'

interface UseInvoiceDetailActionsParams {
    invoiceId: number
    invoice: InvoiceDetail | undefined
}

export const useInvoiceDetailActions = ({
    invoiceId,
    invoice,
}: UseInvoiceDetailActionsParams) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const { refetch } = useQueryClientCallback({
        mode: 'contains',
        pattern: QUERY_KEYS.INVOICES,
    })

    const { mutateAsync: approved, isPending: isApprovePending } =
        useMutationWithHandler(
            { mutationFn: approvedInvoice },
            {
                reactQueryKeys: QUERY_KEYS.INVOICES,
                successMessage: t('approved-invoice-success'),
                onSuccess: () => refetch(),
            }
        )

    const { mutateAsync: deletedMutation, isPending: isDeletePending } =
        useMutationWithHandler(
            {
                mutationFn: ({
                    invoiceId,
                    category,
                    message,
                }: {
                    invoiceId: number
                    category: string
                    message: string
                }) => deleteInvoice(invoiceId, category, message),
            },
            {
                reactQueryKeys: QUERY_KEYS.INVOICES,
                successMessage: t('deleted-invoice-success'),
                onSuccess: () => {
                    refetch()
                    navigate(paths.invoices)
                },
            }
        )

    const {
        mutateAsync: cancalledInvoiceMutation,
        isPending: isCancalledPending,
    } = useMutationWithHandler(
        {
            mutationFn: ({
                invoiceId,
                category,
                message,
            }: {
                invoiceId: number
                category: string
                message: string
            }) => cancalledInvoice(invoiceId, category, message),
        },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            successMessage: t('cancelled-invoice-success'),
            onSuccess: () => refetch(),
        }
    )

    const handleConfirmAction = (
        actionCallBack: () => void,
        action: string
    ) => {
        setConfirmation({
            open: true,
            title: t('confirm'),
            description: t(`${action}-invoice-description`),
            onConfirm: actionCallBack,
        })
    }

    const status = invoice?.invoice_status

    const actions: Action[] = [
        {
            'data-tour': 'action-non-invoicable',
            icon: <NonInvoicableIcons />,
            label: t('non-invoicable'),
            onClick() {
                handleConfirmAction(() => approved({ invoiceId }), 'approved')
            },
            color: 'default',
            variant: 'outlined',
            disabled: isApprovePending || !isInvoiceConfirmable(invoice),
        },
        {
            'data-tour': 'action-approve',
            icon: <CheckIcons />,
            label: t('invoice-approved'),
            onClick() {
                handleConfirmAction(
                    () =>
                        approved({
                            invoiceId,
                            approvedMethod: 'invoicable',
                        }),
                    'approved'
                )
            },
            color: 'success',
            disabled: isApprovePending || !isInvoiceConfirmable(invoice),
        },
        {
            'data-tour': 'action-pay',
            icon: <PaidIcons />,
            label: t('paid-invoice'),
            onClick() {
                setModal('InvoiceModals/PaidInvoiceModal/index', {
                    invoice,
                })
            },
            color: 'success',
            disabled: INVOICE_STATUS_OPTIONS.Approved !== status,
        },
        {
            'data-tour': 'action-cancel',
            label: t('cancalled-invoice'),
            onClick() {
                setModal<CancelOrDeleteProps>(
                    'InvoiceModals/CancelOrDelete/index',
                    {
                        invoiceID: invoiceId,
                        onSubmit: (category: string, message: string) =>
                            cancalledInvoiceMutation({
                                invoiceId,
                                category,
                                message,
                            }),
                    }
                )
            },
            variant: 'outlined',
            icon: <BlockIcons />,
            color: 'default',
            disabled:
                isCancalledPending ||
                INVOICE_STATUS_OPTIONS.WaitingConfirm !== status,
        },
        {
            'data-tour': 'action-delete',
            label: t('delete-invoice'),
            onClick() {
                setModal<CancelOrDeleteProps>(
                    'InvoiceModals/CancelOrDelete/index',
                    {
                        invoiceID: invoiceId,
                        onSubmit: (category: string, message: string) =>
                            deletedMutation({
                                invoiceId,
                                category,
                                message,
                            }),
                    }
                )
            },
            color: 'error',
            icon: <DeleteOutlineIcons />,
            disabled:
                isDeletePending ||
                INVOICE_STATUS_OPTIONS.WaitingConfirm !== status,
        },
        {
            'data-tour': 'action-preview-mail',
            label: t('preview-mail-statement'),
            variant: 'outlined',
            icon: <MailIcons />,
            onClick() {
                setModal<PreviewInvoiceModalProps>(
                    'InvoiceModals/PreviewInvoice/index',
                    { type: 'statement', invoiceID: invoiceId }
                )
            },
            disabled: invoice?.invoice_type === 'deposit',
        },
        // Chứng từ ký số: giấy đề nghị thanh toán, biên bản đối soát (mỗi loại 1 nút)
        ...SIGNED_DOCUMENT_TYPES.map((documentType): Action => {
            const { statusField, i18nKey } = SIGNED_DOCUMENTS[documentType]
            return {
                'data-tour': `action-${documentType}`,
                label: t(
                    invoice?.[statusField] === 'signed'
                        ? `${i18nKey}.view-signed`
                        : `${i18nKey}.create`,
                    { ns: 'invoice' }
                ),
                variant: 'outlined',
                icon: <FileSignature className="w-5 h-5" />,
                onClick() {
                    setModal<SignedDocumentModalProps>(
                        'InvoiceModals/SignedDocument/index',
                        {
                            documentType,
                            invoiceID: invoiceId,
                            invoiceStatus: status,
                        }
                    )
                },
                disabled: invoice?.invoice_type === 'deposit',
            }
        }),
        {
            'data-tour': 'action-send-mail',
            label: t('send-mail'),
            variant: 'outlined',
            icon: <SendIcons />,
            onClick() {
                setModal<SendMailModalProps>('InvoiceModals/SendMail/index', {
                    invoiceID: invoiceId,
                })
            },
            disabled: invoice?.invoice_type === 'deposit',
        },
        {
            'data-tour': 'action-add-update',
            label: t('add-invoice-updates'),
            icon: <AddIcons />,
            onClick: () => {
                setModal<AddInvoiceUpdateProps>(
                    'InvoiceModals/AddInvoiceUpdates/index',
                    { invoice_id: invoiceId }
                )
            },
            disabled: false,
        },
        {
            'data-tour': 'action-revenue-allocation',
            label: t('revenue_allocation_preview') || 'Revenue Allocation',
            icon: <BarChart className="w-5 h-5" />,
            variant: 'outlined',
            onClick: () => {
                setModal<RevenueAllocationPreviewProps>(
                    'InvoiceModals/RevenueAllocationPreview/index',
                    { invoiceID: invoiceId }
                )
            },
            disabled: false,
        },
        {
            'data-tour': 'action-rollback',
            label: t('rollback-invoice'),
            icon: <UndoIcons />,
            color: 'error',
            onClick: () => {
                setModal<RollbackInvoiceProps>(
                    'InvoiceModals/RollbackInvoice/index',
                    { invoiceID: invoiceId }
                )
            },
            disabled:
                status !== INVOICE_STATUS_OPTIONS.Paid &&
                status !== INVOICE_STATUS_OPTIONS.Done,
        },
    ]

    return { actions }
}
